import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Plus, Target, BookOpen, ArrowRight, ArrowUp, Palette, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageCard from './PageCard';
import PageCardSkeleton from './PageCardSkeleton';

interface Page {
  id: string;
  title: string;
  type: string;
  updated_at: string;
  content?: string;
  workbook_id?: string | null; // NEW: Add workbook_id for project detection
}

interface PageGridProps {
  pages: Page[];
  onPageEdit: (pageId: string) => void;
  onCreatePage?: () => void;
  isLoading?: boolean;
  hideProjectButton?: boolean; // NEW: Optional prop to hide Add to Project button
  searchTerm?: string; // NEW: Search term for no results messaging
}

const PageGrid: React.FC<PageGridProps> = ({ 
  pages, 
  onPageEdit, 
  onCreatePage, 
  isLoading = false,
  hideProjectButton = false,
  searchTerm
}) => {
  // Use the passed hideProjectButton prop instead of location detection
  // This provides more explicit control over button visibility

  // ✅ FIX: Show skeleton loading during search operations (like other generators)
  if (isLoading && pages.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <PageCardSkeleton key={`skeleton-${index}`} index={index} />
        ))}
      </div>
    );
  }

  // ✅ FIX: Always show the same empty state regardless of search/filter
  if (pages.length === 0) {
    // Always show the "Your Documents" empty state
    const EmptyStateContent = () => {
      const [isAccordionMode, setIsAccordionMode] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth < 1024; // lg breakpoint
      });

      useEffect(() => {
        const mql = window.matchMedia('(max-width: 1023px)');
        
        const handleChange = (e: MediaQueryListEvent) => {
          setIsAccordionMode(e.matches);
        };

        mql.addEventListener('change', handleChange);
        setIsAccordionMode(mql.matches);

        return () => {
          mql.removeEventListener('change', handleChange);
        };
      }, []);

      const ArrowIcon = isAccordionMode ? ArrowUp : ArrowRight;
      const actionText = isAccordionMode ? 'menu' : 'sidebar';

      return (
        <div className="space-y-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col items-center justify-center py-16 px-6 text-center"
          >
            {/* Beautiful icon with gradient background */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-red-100 to-red-100 dark:from-red-900/30 dark:to-red-900/30 flex items-center justify-center shadow-lg border border-red-200/30 dark:border-red-700/30"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/10 to-red-500/10 dark:from-red-400/20 dark:to-red-400/20"></div>
              <FileText className="w-10 h-10 text-red-600 dark:text-red-400 relative z-10" />
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3] 
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut" 
                }}
                className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-400/20 to-red-400/20"
              />
            </motion.div>

            {/* Title */}
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
            >
              Your Documents
            </motion.h2>

            {/* Description */}
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-gray-600 dark:text-gray-300 mb-8 max-w-lg leading-relaxed"
            >
              Let AI transform your random thoughts into content gold that actually gets read. Zero writer's block, maximum impact.
            </motion.p>

            {/* Different content based on context */}
            {onCreatePage ? (
              // Project pages context - show button-based empty state
              <>
                {/* Benefits grid */}
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <div className="flex flex-col items-center text-center p-3">
                    <Target className="h-8 w-8 text-red-500 dark:text-red-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Stay Organized</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Structure content</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-3">
                    <Palette className="h-8 w-8 text-red-500 dark:text-red-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Create Content</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">AI-powered writing</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-3">
                    <Eye className="h-8 w-8 text-red-500 dark:text-red-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Engage Readers</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Captivate audience</p>
                  </div>
                </motion.div>

                {/* Call-to-action button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <Button 
                    onClick={onCreatePage}
                    className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
                    
                    {/* Button content */}
                    <div className="relative flex items-center justify-center gap-3">
                      <Plus className="w-5 h-5" />
                      <span>Create Your First Document</span>
                    </div>
                  </Button>
                </motion.div>

                {/* Subtle hint */}
                <motion.p 
                  className="text-xs text-gray-400 dark:text-gray-500 mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  Documents you create will appear here
                </motion.p>
              </>
            ) : (
              // Private text generator context - show sidebar directions (similar to private-research-generator)
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex items-center gap-3 px-6 py-3 rounded-xl bg-gradient-to-r from-red-50 to-red-50 dark:from-red-900/20 dark:to-red-900/20 border border-red-200/40 dark:border-red-700/40"
            >
              <span className="text-sm font-medium text-white">
                Use the Search & Create {actionText} to start
              </span>
              <motion.div
                animate={isAccordionMode ? { 
                  y: [0, -3, 0],
                } : {
                  x: [0, 3, 0],
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut" 
                }}
              >
                <ArrowIcon className="w-4 h-4 text-white" />
              </motion.div>
            </motion.div>
            )}
          </motion.div>
        </div>
      );
    };

    return <EmptyStateContent />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {pages.map((page, index) => (
        <motion.div
          key={page.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1, delay: index * 0.01 }}
        >
          <PageCard 
            page={page} 
            onEdit={onPageEdit}
            hideProjectButton={hideProjectButton}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default PageGrid; 